"use client";

import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { useLocalStorageState } from "@/lib/useLocalStorageState";
import { cn } from "@/lib/utils";

export const APP_MAIN_FULL_WIDTH_KEY = "radium-app-main-full-width";

type AppMainWidthContextValue = {
  /** When true, main column uses full inset width; when false, a centered max width is applied. */
  fullWidth: boolean;
  setFullWidth: (value: boolean) => void;
  toggleFullWidth: () => void;
};

const AppMainWidthContext = createContext<AppMainWidthContextValue | null>(null);

export const useAppMainWidth = (): AppMainWidthContextValue => {
  const ctx = useContext(AppMainWidthContext);
  if (!ctx) {
    throw new Error("useAppMainWidth must be used within AppMainWidthProvider.");
  }
  return ctx;
};

export const AppMainWidthProvider = ({ children }: { children: ReactNode }) => {
  // Avoid reading localStorage during the first client render so SSR HTML matches
  // hydration. The persisted value syncs after mount via useLocalStorage's effect.
  // Without this, the SiteHeader's expand/collapse icon and AppInsetBody's max-width
  // class differ between SSR (default) and the client's first render (localStorage),
  // which shifts every downstream BaseUI useId() counter and surfaces as a
  // hydration mismatch on the NavUser DropdownMenuTrigger button.
  const [fullWidth, setFullWidth] = useLocalStorageState<boolean>(APP_MAIN_FULL_WIDTH_KEY, false, {
    dataType: "boolean",
    initializeWithValue: false,
  });

  const toggleFullWidth = useCallback(() => {
    setFullWidth((v) => !v);
  }, [setFullWidth]);

  const value = useMemo(
    () => ({ fullWidth, setFullWidth, toggleFullWidth }),
    [fullWidth, setFullWidth, toggleFullWidth],
  );

  return <AppMainWidthContext.Provider value={value}>{children}</AppMainWidthContext.Provider>;
};

interface AppInsetBodyProps {
  children: ReactNode;
  className?: string;
}

/**
 * Main scroll column below {@link SiteHeader}. Applies the user’s preferred max width
 * (or full width) when wrapped with {@link AppMainWidthProvider}, plus shared page
 * content padding (half of the former `p-4 md:p-8` / `px-4 lg:px-6` page wrappers).
 */
export const AppInsetBody = ({ children, className }: AppInsetBodyProps) => {
  const { fullWidth } = useAppMainWidth();

  return (
    <div
      data-slot="app-inset-body"
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col p-2 md:p-4",
        fullWidth ? "w-full" : "mx-auto w-full max-w-6xl 2xl:max-w-7xl",
        className,
      )}
    >
      {children}
    </div>
  );
};
